import * as React from 'react';

import { ChevronDown } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { ButtonGroup } from '../../lib/button-group.jsx';
import { Button } from '../../lib/button.jsx';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '../../lib/popover.jsx';
import { itemVariants } from '../../lib/variants.jsx';

type SplitButtonSharedProps = Pick<
  React.ComponentProps<typeof Button>,
  'variant' | 'color' | 'size' | 'disabled'
>;

type SplitButtonContextValue = SplitButtonSharedProps & {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const SplitButtonContext = React.createContext<SplitButtonContextValue | null>(
  null,
);

function useSplitButtonContext(slot: string) {
  const context = React.useContext(SplitButtonContext);

  if (!context) {
    throw new Error(`\`${slot}\` must be used within a \`SplitButton\``);
  }

  return context;
}

function SplitButton({
  children,
  className,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  open,
  defaultOpen = false,
  onOpenChange,
  modal,
  ...props
}: Readonly<
  Omit<React.ComponentProps<typeof ButtonGroup>, 'color' | 'orientation'> &
    SplitButtonSharedProps &
    Pick<
      React.ComponentProps<typeof Popover>,
      'open' | 'defaultOpen' | 'onOpenChange' | 'modal'
    >
>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

  const isOpen = open ?? uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (open === undefined) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [open, onOpenChange],
  );

  const contextValue = React.useMemo(
    () => ({
      variant,
      color,
      size,
      disabled,
      open: isOpen,
      setOpen: handleOpenChange,
    }),
    [variant, color, size, disabled, isOpen, handleOpenChange],
  );

  return (
    <SplitButtonContext.Provider value={contextValue}>
      <Popover open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
        <ButtonGroup
          data-slot="split-button"
          className={cn(variant === 'contained' && 'gap-px', className)}
          {...props}
        >
          {children}
        </ButtonGroup>
      </Popover>
    </SplitButtonContext.Provider>
  );
}

function SplitButtonAction({
  className,
  disabled,
  ...props
}: Readonly<
  Omit<React.ComponentProps<typeof Button>, 'variant' | 'color' | 'size'>
>) {
  const context = useSplitButtonContext('SplitButtonAction');

  return (
    <Button
      data-slot="split-button-action"
      variant={context.variant}
      color={context.color}
      size={context.size}
      disabled={context.disabled || disabled}
      className={className}
      {...props}
    />
  );
}

function SplitButtonTrigger({
  className,
  disabled,
  'aria-label': ariaLabel = 'More options',
  ...props
}: Readonly<
  Omit<React.ComponentProps<typeof Button>, 'variant' | 'color' | 'size'>
>) {
  const context = useSplitButtonContext('SplitButtonTrigger');

  return (
    <PopoverTrigger asChild>
      <Button
        icon
        data-slot="split-button-trigger"
        variant={context.variant}
        color={context.color}
        size={context.size}
        disabled={context.disabled || disabled}
        aria-label={ariaLabel}
        className={className}
        {...props}
      >
        <ChevronDown />
      </Button>
    </PopoverTrigger>
  );
}

function SplitButtonContent({
  className,
  align = 'end',
  sideOffset = 4,
  container,
  ...props
}: Readonly<
  React.ComponentProps<typeof PopoverContent> & {
    /**
     * Specify a container element to portal the content into.
     * @default document.body
     */
    container?: Exclude<
      React.ComponentProps<typeof PopoverPortal>['container'],
      undefined
    >;
  }
>) {
  return (
    <PopoverPortal container={container}>
      <PopoverContent
        data-slot="split-button-content"
        align={align}
        sideOffset={sideOffset}
        className={cn('z-popover flex w-fit min-w-40 flex-col py-1', className)}
        {...props}
      />
    </PopoverPortal>
  );
}

function SplitButtonItem({
  className,
  selected = false,
  closeOnSelect = true,
  type = 'button',
  onClick,
  ...props
}: Readonly<
  React.ComponentProps<'button'> & {
    /** Highlights the item as the currently selected one. */
    selected?: boolean;
    /** Closes the popover after the item is clicked. */
    closeOnSelect?: boolean;
  }
>) {
  const { setOpen } = useSplitButtonContext('SplitButtonItem');

  return (
    <button
      data-slot="split-button-item"
      type={type}
      className={cn(
        itemVariants({ popoverItem: selected ? 'selected' : 'default' }),
        'text-left',
        className,
      )}
      onClick={(event) => {
        onClick?.(event);

        if (closeOnSelect && !event.defaultPrevented) {
          setOpen(false);
        }
      }}
      {...props}
    />
  );
}

export {
  SplitButton,
  SplitButtonAction,
  SplitButtonTrigger,
  SplitButtonContent,
  SplitButtonItem,
};
